import React, { useEffect, useState } from 'react';
import { Paperclip, Download, FileText, Image as ImageIcon, Film, FileArchive, X } from 'lucide-react';
import { signedAttachmentUrls } from '../../lib/db';

const IMAGE_MIME = /^image\//i;
const VIDEO_MIME = /^video\//i;
const PDF_MIME   = /pdf$/i;
const ZIP_MIME   = /(zip|rar|7z|tar|gzip)/i;

function iconFor(mime) {
  if (IMAGE_MIME.test(mime || '')) return ImageIcon;
  if (VIDEO_MIME.test(mime || '')) return Film;
  if (ZIP_MIME.test(mime || ''))   return FileArchive;
  if (PDF_MIME.test(mime || ''))   return FileText;
  return FileText;
}

function bytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Pending-attachments tray shown above the message input while composing.
 * Renders the file picker icon (handled by the parent), the list of
 * about-to-be-sent files, and a remove button per file.
 */
export function PendingAttachments({ pending, onRemove }) {
  if (!pending?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-2 pb-2">
      {pending.map((p, idx) => {
        const Icon = iconFor(p.mime || p.file?.type);
        return (
          <div
            key={idx}
            className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 max-w-[220px]"
            title={p.file?.name}
          >
            <Icon size={12} className="text-slate-500 flex-shrink-0" />
            <span className="truncate flex-1">{p.file?.name}</span>
            <span className="text-slate-500">{bytes(p.file?.size)}</span>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 flex-shrink-0"
              aria-label="Remove attachment"
            >
              <X size={11} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Renders the attachments array on a sent message. Resolves a single batch
 * of signed URLs per render so we don't fan out N storage requests when a
 * message has 3 files. Images render as inline thumbnails; everything else
 * is a download chip with mime-appropriate icon.
 */
export default function MessageAttachments({ attachments }) {
  const [urls, setUrls] = useState(() => new Map());

  useEffect(() => {
    if (!Array.isArray(attachments) || attachments.length === 0) return;
    let cancelled = false;
    const paths = attachments.map((a) => a.path).filter(Boolean);
    if (!paths.length) return;
    signedAttachmentUrls(paths).then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      const next = new Map();
      data.forEach((r) => {
        if (r?.path && r?.signedUrl) next.set(r.path, r.signedUrl);
      });
      setUrls(next);
    });
    return () => { cancelled = true; };
  }, [attachments]);

  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((a, idx) => {
        const url = urls.get(a.path);
        const isImage = IMAGE_MIME.test(a.mime || '');
        if (isImage && url) {
          return (
            <a
              key={idx}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="block rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-indigo-500 transition"
              title={a.name}
            >
              <img
                src={url}
                alt={a.name}
                className="max-h-44 max-w-[260px] object-cover"
                loading="lazy"
              />
            </a>
          );
        }
        const Icon = iconFor(a.mime);
        return (
          <a
            key={idx}
            href={url || '#'}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => { if (!url) e.preventDefault(); }}
            className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs max-w-[260px] transition ${
              url
                ? 'border-slate-200 dark:border-slate-700 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-slate-700 dark:text-slate-200'
                : 'border-slate-200 dark:border-slate-800 text-slate-400 cursor-progress'
            }`}
            title={a.name}
          >
            <Icon size={13} className="flex-shrink-0" />
            <span className="truncate flex-1">{a.name}</span>
            <span className="text-slate-500 flex-shrink-0">{bytes(a.size)}</span>
            {url ? <Download size={11} className="text-slate-500 flex-shrink-0" /> : null}
          </a>
        );
      })}
    </div>
  );
}

/**
 * Compact paperclip button + hidden file input. Parent passes onSelect
 * which receives an array of newly-picked files.
 */
export function AttachButton({ onSelect, disabled, multiple = true }) {
  const inputRef = React.useRef(null);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        title="Attach files"
        className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 transition"
      >
        <Paperclip size={16} />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onSelect(files);
          e.target.value = '';
        }}
      />
    </>
  );
}
